/**
 * The slice of Markdown the two legal documents use: headings, paragraphs,
 * bullets and tables.
 *
 * Split out of `LegalDocument.tsx` so that file exports nothing but its
 * component — a rule the repo's lint keeps, and the reason fast refresh works.
 */

export type Block =
  | { kind: 'h'; level: 1 | 2; text: string }
  | { kind: 'p'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'table'; head: string[]; rows: string[][] };

const CELLS = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim().replace(/\\\|/g, '|'));

const isDivider = (line: string): boolean => /^\|[\s:|-]+\|$/.test(line.trim());

export function parseLegal(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const heading = /^(#{1,2})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push({ kind: 'h', level: heading[1].length as 1 | 2, text: heading[2] });
      continue;
    }

    if (line.startsWith('|') && isDivider(lines[i + 1] ?? '')) {
      const head = CELLS(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        rows.push(CELLS(lines[i]));
        i++;
      }
      i--;
      blocks.push({ kind: 'table', head, rows });
      continue;
    }

    const bullet = /^[-*]\s+(.*)$/.exec(line);
    if (bullet) {
      const last = blocks[blocks.length - 1];
      if (last?.kind === 'ul') last.items.push(bullet[1]);
      else blocks.push({ kind: 'ul', items: [bullet[1]] });
      continue;
    }

    blocks.push({ kind: 'p', text: line });
  }

  return blocks;
}

/** A numbered heading's anchor: "## 7. Google Calendar sync" → `#7`. */
export const headingId = (text: string): string | undefined => {
  const numbered = /^(\d+)\./.exec(text);
  return numbered ? numbered[1] : undefined;
};
